import { Plus, Trash2, Edit2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { TacticalSpecialist, ProjectStatus } from '@/types/project';
import { RevenueType } from '@/types/crm';
import { PeopleSelect } from '@/components/PeopleSelect';
import { PeopleMultiSelect } from '@/components/PeopleMultiSelect';
import { formatCurrency, formatCurrencyInput, parseCurrencyInput } from '../utils';
import { DEFAULT_TACTICAL_SPECIALISTS } from '../constants';
import { useState } from 'react';
import { Calendar, DollarSign } from 'lucide-react';

// Get next 3 months for ad-hoc revenue selection
const getMonthOptions = () => {
  const months = [];
  for (let i = 0; i < 3; i++) {
    const date = new Date();
    date.setMonth(date.getMonth() + i);
    const value = date.toISOString().substring(0, 7);
    const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    months.push({ value, label });
  }
  return months;
};

interface TeamCostsTabProps {
  // Project Setup
  projectName: string;
  setProjectName: (value: string) => void;
  projectStatus: ProjectStatus;
  setProjectStatus: (value: ProjectStatus) => void;
  revenue: number;
  setRevenue: (value: number) => void;

  // Revenue Type (for variable/ad-hoc clients)
  revenueType: RevenueType;
  onRevenueTypeChange: (value: RevenueType) => void;
  clientId?: string | null;
  adHocMonth: string;
  setAdHocMonth: (value: string) => void;
  adHocAmount: number;
  setAdHocAmount: (value: number) => void;
  onSaveAdHocRevenue: () => void;
  savingAdHoc: boolean;

  // Revenue Allocation
  profitMargin: number;
  setProfitMargin: (value: number) => void;
  operationsOverheadPercentage: number;
  setOperationsOverheadPercentage: (value: number) => void;
  baseProfit: number;
  operationsCost: number;
  availableTacticalBudget: number;

  // Team
  strategySponsor: string;
  setStrategySponsor: (value: string) => void;
  operationsOwners: string[];
  setOperationsOwners: (value: string[]) => void;
  operationsCompensationPerOwner: number;
  setOperationsCompensationPerOwner: (value: number) => void;
  totalOperationsCompensation: number;

  // People
  people: string[];
  addPerson: (name: string) => Promise<{ name: string } | null>;

  // Tactical Specialists
  tacticalSpecialists: TacticalSpecialist[];
  addTacticalSpecialist: (name?: string) => void;
  removeTacticalSpecialist: (id: string) => void;
  updateTacticalSpecialist: (id: string, field: keyof TacticalSpecialist, value: string | number | boolean | undefined) => void;
  baseTacticalCosts: number;
  tacticalBudgetRemaining: number;

  // Kicker calculations for display
  getSpecialistKickerEarnings: (specialistId: string) => number;
}

export function TeamCostsTab({
  projectName,
  setProjectName,
  projectStatus,
  setProjectStatus,
  revenue,
  setRevenue,
  revenueType,
  onRevenueTypeChange,
  clientId,
  adHocMonth,
  setAdHocMonth,
  adHocAmount,
  setAdHocAmount,
  onSaveAdHocRevenue,
  savingAdHoc,
  profitMargin,
  setProfitMargin,
  operationsOverheadPercentage,
  setOperationsOverheadPercentage,
  baseProfit,
  operationsCost,
  availableTacticalBudget,
  strategySponsor,
  setStrategySponsor,
  operationsOwners,
  setOperationsOwners,
  operationsCompensationPerOwner,
  setOperationsCompensationPerOwner,
  totalOperationsCompensation,
  people,
  addPerson,
  tacticalSpecialists,
  addTacticalSpecialist,
  removeTacticalSpecialist,
  updateTacticalSpecialist,
  baseTacticalCosts,
  tacticalBudgetRemaining,
  getSpecialistKickerEarnings,
}: TeamCostsTabProps) {
  const [editingSpecialist, setEditingSpecialist] = useState<TacticalSpecialist | null>(null);

  return (
    <div className="space-y-4 md:space-y-6">
      <Accordion type="multiple" defaultValue={['project-setup', 'tactical-specialists']} className="w-full space-y-4">
        {/* Project Setup */}
        <AccordionItem value="project-setup" className="border rounded-lg">
          <AccordionTrigger className="px-3 sm:px-6 hover:no-underline">
            <span className="text-sm md:text-base font-medium font-serif">Project Setup</span>
          </AccordionTrigger>
          <AccordionContent className="px-3 sm:px-6 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 lg:gap-6">
              <div>
                <Label htmlFor="project-name">Project Name</Label>
                <Input
                  id="project-name"
                  placeholder="Enter project name"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  className={!projectName.trim() ? "border-destructive/50 focus:border-destructive" : ""}
                />
                {!projectName.trim() && (
                  <p className="text-sm text-destructive mt-1">Required</p>
                )}
              </div>
              <div>
                <Label htmlFor="project-status">Status</Label>
                <Select value={projectStatus} onValueChange={(value: ProjectStatus) => setProjectStatus(value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="prospective">Prospective</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="revenue">Monthly Retainer</Label>
                <Input
                  id="revenue"
                  type="text"
                  placeholder="$0"
                  value={revenue > 0 ? formatCurrencyInput(revenue) : ''}
                  onChange={(e) => setRevenue(parseCurrencyInput(e.target.value))}
                  disabled={revenueType === 'variable' || revenueType === 'one_off'}
                />
                {revenueType === 'variable' && (
                  <p className="text-xs text-muted-foreground mt-1">Set via ad-hoc entry below</p>
                )}
                {revenueType === 'one_off' && (
                  <p className="text-xs text-muted-foreground mt-1">One-off deals carry no monthly retainer</p>
                )}
              </div>
            </div>

            {/* Revenue Type - only show for clients */}
            {clientId && (
              <div className="mt-4 pt-4 border-t">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Revenue Type</Label>
                    <Select value={revenueType} onValueChange={(v) => onRevenueTypeChange(v as RevenueType)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="recurring">Recurring - Fixed monthly retainer</SelectItem>
                        <SelectItem value="variable">Variable - Ad-hoc payments per month</SelectItem>
                        <SelectItem value="one_off">One-Off - Single payment, not in MRR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Ad-hoc revenue entry - only show for variable */}
                  {revenueType === 'variable' && (
                    <div className="md:col-span-2 p-4 border rounded-lg bg-muted/50 space-y-4">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Calendar className="h-4 w-4" />
                        Ad-Hoc Revenue Entry
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="adHocMonth">Month</Label>
                          <Select value={adHocMonth} onValueChange={setAdHocMonth}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {getMonthOptions().map((m) => (
                                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="adHocAmount">Amount</Label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="adHocAmount"
                              type="number"
                              className="pl-9"
                              value={adHocAmount || ''}
                              onChange={(e) => setAdHocAmount(Number(e.target.value))}
                            />
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={onSaveAdHocRevenue}
                        disabled={savingAdHoc || !adHocAmount || adHocAmount <= 0}
                        className="w-full"
                      >
                        {savingAdHoc ? 'Saving...' : 'Save Ad-Hoc Revenue'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>

        {/* Revenue Allocation */}
        <AccordionItem value="revenue-allocation" className="border rounded-lg">
          <AccordionTrigger className="px-3 sm:px-6 hover:no-underline">
            <span className="text-sm md:text-base font-medium font-serif">Revenue Allocation</span>
          </AccordionTrigger>
          <AccordionContent className="px-3 sm:px-6 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              <div>
                <Label>Owner Margin: {profitMargin}%</Label>
                <div className="mt-2">
                  <Slider
                    min={0}
                    max={100}
                    step={5}
                    value={[profitMargin]}
                    onValueChange={(value) => setProfitMargin(value[0])}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>0%</span>
                    <span>50%</span>
                    <span>100%</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Taken as owner profit BEFORE costs
                </p>
              </div>
              <div>
                <Label>Operations Overhead: {operationsOverheadPercentage}%</Label>
                <div className="mt-2">
                  <Slider
                    min={0}
                    max={20}
                    step={0.5}
                    value={[operationsOverheadPercentage]}
                    onValueChange={(value) => setOperationsOverheadPercentage(value[0])}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>0%</span>
                    <span>10%</span>
                    <span>20%</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Base operational overhead
                </p>
              </div>
              <div>
                <Label>Revenue Breakdown</Label>
                <div className="space-y-2 text-sm mt-2">
                  <div className="flex justify-between">
                    <span>Base Profit:</span>
                    <span className="font-medium font-serif">{formatCurrency(baseProfit)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Operations:</span>
                    <span className="font-medium font-serif">{formatCurrency(operationsCost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tactical Budget:</span>
                    <span className="font-medium font-serif">{formatCurrency(availableTacticalBudget)}</span>
                  </div>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Team Assignments */}
        <AccordionItem value="team-assignments" className="border rounded-lg">
          <AccordionTrigger className="px-3 sm:px-6 hover:no-underline">
            <span className="text-sm md:text-base font-medium font-serif">Team Assignments</span>
          </AccordionTrigger>
          <AccordionContent className="px-3 sm:px-6 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <Label>Project Strategist</Label>
                <PeopleSelect
                  value={strategySponsor}
                  options={people}
                  onChange={setStrategySponsor}
                  onAddPerson={async (name) => {
                    await addPerson(name);
                    setStrategySponsor(name);
                  }}
                  allowNone={true}
                  placeholder="Select strategist"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Oversight & escalation
                </p>
              </div>

              <div className="space-y-3">
                <Label>Operations Owners</Label>
                <div>
                  <Label className="text-xs">Monthly Compensation</Label>
                  <Input
                    type="text"
                    placeholder="$2,500"
                    value={operationsCompensationPerOwner > 0 ? formatCurrencyInput(operationsCompensationPerOwner) : ''}
                    onChange={(e) => setOperationsCompensationPerOwner(parseCurrencyInput(e.target.value))}
                    className="mt-1"
                  />
                </div>
                <PeopleMultiSelect
                  values={operationsOwners}
                  options={people}
                  onChange={setOperationsOwners}
                  onAddPerson={async (name) => {
                    await addPerson(name);
                    setOperationsOwners([...operationsOwners, name]);
                  }}
                  placeholder="Select operations owners"
                />
                {operationsOwners.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2">
                      {operationsOwners.map((owner) => (
                        <Badge key={owner} variant="secondary" className="flex items-center gap-1">
                          {owner} - {formatCurrency(operationsCompensationPerOwner)}
                          <button
                            onClick={() => setOperationsOwners(operationsOwners.filter(o => o !== owner))}
                            className="ml-1 text-xs hover:text-destructive"
                          >
                            ×
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Total: {formatCurrency(totalOperationsCompensation)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Team Members */}
        <AccordionItem value="tactical-specialists" className="border rounded-lg">
          <AccordionTrigger className="px-3 sm:px-6 hover:no-underline">
            <div className="flex items-center gap-2">
              <span className="text-sm md:text-base font-medium font-serif">Team Members</span>
              <Badge variant="secondary" className="text-xs">
                {tacticalSpecialists.length}
              </Badge>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-0 md:px-4 lg:px-6 pb-4">
            <div className="flex flex-wrap gap-2 mb-4 px-3 md:px-0">
              <Select onValueChange={(value) => addTacticalSpecialist(value)}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="Add preset" />
                </SelectTrigger>
                <SelectContent>
                  {DEFAULT_TACTICAL_SPECIALISTS.map((specialist) => (
                    <SelectItem key={specialist} value={specialist}>
                      {specialist}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={() => addTacticalSpecialist()}>
                <Plus className="h-4 w-4 mr-1" />
                <span className="hidden md:inline">Custom</span>
              </Button>
            </div>

            {tacticalSpecialists.length > 0 && (
              <>
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs lg:text-sm">Name</TableHead>
                        <TableHead className="text-xs lg:text-sm">Amount</TableHead>
                        <TableHead className="text-xs lg:text-sm">Assigned To</TableHead>
                        <TableHead className="text-xs lg:text-sm">Bonus Eligible</TableHead>
                        <TableHead className="text-xs lg:text-sm">Bonus Earnings</TableHead>
                        <TableHead className="w-16 lg:w-20"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tacticalSpecialists.map((specialist) => {
                        const kickerEarnings = getSpecialistKickerEarnings(specialist.id);
                        return (
                          <TableRow key={specialist.id}>
                            <TableCell>
                              <Input
                                placeholder="Name"
                                value={specialist.name}
                                onChange={(e) => updateTacticalSpecialist(specialist.id, 'name', e.target.value)}
                                className="max-w-40"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="text"
                                placeholder="$0"
                                value={specialist.amount > 0 ? formatCurrencyInput(specialist.amount) : ''}
                                onChange={(e) => updateTacticalSpecialist(specialist.id, 'amount', parseCurrencyInput(e.target.value))}
                                className="max-w-24"
                              />
                            </TableCell>
                            <TableCell>
                              <PeopleSelect
                                value={specialist.assignedOwner || ''}
                                options={people}
                                onChange={(value) => updateTacticalSpecialist(specialist.id, 'assignedOwner', value || undefined)}
                                onAddPerson={async (name) => {
                                  await addPerson(name);
                                  updateTacticalSpecialist(specialist.id, 'assignedOwner', name);
                                }}
                                allowNone={true}
                                placeholder="None"
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={specialist.handlesKickers}
                                  onCheckedChange={(checked) => updateTacticalSpecialist(specialist.id, 'handlesKickers', checked)}
                                />
                                <span className="text-xs text-muted-foreground">
                                  {specialist.handlesKickers ? 'Yes' : 'No'}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className={kickerEarnings > 0 ? 'font-medium text-positive' : 'text-muted-foreground'}>
                                {kickerEarnings > 0 ? formatCurrency(kickerEarnings) : '-'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeTacticalSpecialist(specialist.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden space-y-3 px-3">
                  {tacticalSpecialists.map((specialist) => {
                    const kickerEarnings = getSpecialistKickerEarnings(specialist.id);
                    return (
                      <Card key={specialist.id} className="p-3 sm:p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1 min-w-0 mr-2">
                            <Input
                              placeholder="Specialist Name"
                              value={specialist.name}
                              onChange={(e) => updateTacticalSpecialist(specialist.id, 'name', e.target.value)}
                              className="font-medium"
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeTacticalSpecialist(specialist.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Amount</Label>
                            <Input
                              type="text"
                              placeholder="$0"
                              value={specialist.amount > 0 ? formatCurrencyInput(specialist.amount) : ''}
                              onChange={(e) => updateTacticalSpecialist(specialist.id, 'amount', parseCurrencyInput(e.target.value))}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Assigned To</Label>
                            <PeopleSelect
                              value={specialist.assignedOwner || ''}
                              options={people}
                              onChange={(value) => updateTacticalSpecialist(specialist.id, 'assignedOwner', value || undefined)}
                              onAddPerson={async (name) => {
                                await addPerson(name);
                                updateTacticalSpecialist(specialist.id, 'assignedOwner', name);
                              }}
                              allowNone={true}
                              placeholder="None"
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-3 pt-3 border-t">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={specialist.handlesKickers}
                              onCheckedChange={(checked) => updateTacticalSpecialist(specialist.id, 'handlesKickers', checked)}
                            />
                            <span className="text-xs">Bonus Eligible</span>
                          </div>
                          {kickerEarnings > 0 && (
                            <span className="text-sm font-medium text-positive">
                              {formatCurrency(kickerEarnings)}
                            </span>
                          )}
                        </div>
                      </Card>
                    );
                  })}
                </div>

                {/* Summary Footer */}
                <div className="pt-4 border-t mt-4 px-3 md:px-0">
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-2 sm:gap-4 text-xs md:text-sm">
                    <div className="flex justify-between sm:gap-2">
                      <span>Total Base:</span>
                      <span className="font-medium font-serif">{formatCurrency(baseTacticalCosts)}</span>
                    </div>
                    <div className="flex justify-between sm:gap-2">
                      <span>Budget Remaining:</span>
                      <span className={`font-medium font-serif ${tacticalBudgetRemaining >= 0 ? 'text-positive' : 'text-negative'}`}>
                        {formatCurrency(tacticalBudgetRemaining)}
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
